import { useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, MetricCard, Card } from '../../components/core';
import { Modal, DataTable, type Column, SpinnerBlock } from '../../components/feedback';
import { useUiStore } from '../../store/ui';
import type {
  AdminDashboardDto,
  EmailReminderResultDto,
  StreamingServerDto,
  UpcomingRenewalDto,
  WhatsAppReminderResultDto,
} from '@livestreamx/shared-types';
import { ADMIN_NAV } from './nav';

/* ── SVG icons (wrapped in <svg> for metric-icon chip) ── */
const sv = (children: React.ReactNode) =>
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
const I = {
  tenants: sv(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  active: sv(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
  film: sv(<><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></>),
  live: sv(<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /></>),
  globe: sv(<><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>),
  link: sv(<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>),
  dollar: sv(<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
  file: sv(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>),
};

const fmt = (n: number) => n.toLocaleString();
const fmtCur = (n: number) =>
  n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function LoadBar({ value, max, label }: { value: number | null; max: number; label: string }) {
  const pct = max ? Math.min(100, Math.round(((value ?? 0) / max) * 100)) : 0;
  return (
    <div className="load-cell">
      <span className="text-xs text-muted">{label}: {value != null ? value + '%' : '—'}</span>
      <div className="bar"><span style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

const srvCols: Column<StreamingServerDto>[] = [
  { key: 'name', header: 'Server', render: (s) => <span className="font-medium">{s.name}</span> },
  { key: 'region', header: 'Region', render: (s) => <span className="text-muted text-sm">{s.region}</span> },
  { key: 'status', header: 'Status', render: (s) => {
    const cls = s.status === 'ONLINE' ? 'status-live' : s.status === 'WARNING' ? 'status-warning' : 'status-offline';
    return <span className={`badge ${cls}`}><span className="dot" />{s.status}</span>;
  }},
  { key: 'streams', header: 'Streams', render: (s) => <span className="text-sm"><b>{s.activeStreams}</b> / {s.capacity}</span> },
  { key: 'cpu', header: 'CPU', render: (s) => <LoadBar value={s.cpuUsage} max={100} label="CPU" /> },
  { key: 'mem', header: 'Memory', render: (s) => <LoadBar value={s.memoryUsage} max={100} label="Mem" /> },
];

const renewCols: Column<UpcomingRenewalDto>[] = [
  { key: 'tenant', header: 'Tenant', render: (r) => <span className="font-medium">{r.tenantName}</span> },
  { key: 'plan', header: 'Plan', render: (r) => <span className="badge status-active">{r.planName}</span> },
  { key: 'amount', header: 'Amount', render: (r) => <span>{r.amount.toLocaleString('en-IN', { style: 'currency', currency: r.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span> },
  { key: 'cycle', header: 'Cycle', render: (r) => <span className="text-muted text-sm">{r.billingCycle ? r.billingCycle.toLowerCase() : '—'}</span> },
  {
    key: 'renews', header: 'Renews', render: (r) => {
      const days = Math.max(0, Math.ceil((new Date(r.renewsAt).getTime() - Date.now()) / 86_400_000));
      return (
        <span className="text-sm">
          {new Date(r.renewsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          <span className={`badge ${days <= 7 ? 'status-warning' : 'status-live'}`} style={{ marginLeft: 6 }}>in {days}d</span>
        </span>
      );
    },
  },
];

export function AdminDashboardPage() {
  const ui = useUiStore();
  const [data, setData] = useState<AdminDashboardDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<UpcomingRenewalDto | null>(null);
  const [busy, setBusy] = useState<'email' | 'whatsapp' | null>(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    http.get<AdminDashboardDto>('/api/admin/dashboard')
      .then(setData).catch((e) => setError(e.message ?? 'Failed')).finally(() => setLoading(false));
  }, []);

  const sendEmail = async (r: UpcomingRenewalDto) => {
    setBusy('email'); setNotice('');
    try {
      const res = await http.post<EmailReminderResultDto>(`/api/admin/tenants/${r.tenantId}/renewal-reminder`, { channel: 'email' });
      const text = res.sentVia === 'smtp' ? 'Reminder email sent' : 'Email logged to API console (no SMTP configured)';
      setNotice(text); ui.toast('success', 'Email ' + (res.sentVia === 'smtp' ? 'sent' : 'queued'));
    } catch (e) { setNotice('Error: ' + ((e as Error).message ?? 'Failed')); }
    setBusy(null);
  };

  const sendWhatsApp = async (r: UpcomingRenewalDto) => {
    setBusy('whatsapp'); setNotice('');
    try {
      const res = await http.post<WhatsAppReminderResultDto>(`/api/admin/tenants/${r.tenantId}/renewal-reminder`, { channel: 'whatsapp' });
      const digits = (r.contactPhone ?? '').replace(/\D/g, '');
      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(res.message)}`, '_blank', 'noopener');
      setNotice('WhatsApp draft opened in a new tab.');
    } catch (e) { setNotice('Error: ' + ((e as Error).message ?? 'Failed')); }
    setBusy(null);
  };

  const close = () => { setSelected(null); setNotice(''); }

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Platform overview</p>
          </div>
        </div>
        {loading && <SpinnerBlock />}
        {error && <div className="alert danger">{error}</div>}
        {data && (<>
          <div className="stats-grid">
            <MetricCard label="Total Tenants" value={fmt(data.totalTenants)} icon={I.tenants} tone="primary" />
            <MetricCard label="Active Tenants" value={fmt(data.activeTenants)} icon={I.active} tone="success" />
            <MetricCard label="Total Streams" value={fmt(data.totalStreams)} icon={I.film} tone="info" />
            <MetricCard label="Live Now" value={fmt(data.liveStreams)} icon={I.live} tone="danger"
              hint={data.liveStreams > 0 ? <><span className="live-dot" /> Streaming now</> : undefined} />
            <MetricCard label="Destinations" value={fmt(data.totalDestinations)} icon={I.globe} tone="info" />
            <MetricCard label="Connected" value={fmt(data.connectedDestinations)} icon={I.link} tone="success" />
            <MetricCard label="Revenue (MTD)" value={fmtCur(data.monthlyRevenue)} icon={I.dollar} tone="primary" />
            <MetricCard label="Active Subscriptions" value={fmt(data.activeSubscriptions)} icon={I.file} tone="warning" />
          </div>
          <Card title={`Upcoming Renewals${data.upcomingRenewals.length ? ` (${data.upcomingRenewals.length})` : ''}`}>
            {data.upcomingRenewals.length ? (
              <DataTable columns={renewCols} rows={data.upcomingRenewals} onRowClick={setSelected} />
            ) : (
              <p className="text-muted text-sm" style={{ margin: 0 }}>No renewals within the next 30 days.</p>
            )}
          </Card>
          {data.streamingServers.length > 0 ? (
            <Card title="Streaming Servers">
              <DataTable columns={srvCols} rows={data.streamingServers} />
            </Card>
          ) : (
            <Card><p className="text-muted text-sm" style={{ margin: 0 }}>No streaming servers registered.</p></Card>
          )}
        </>)}
        {selected && (
          <Modal title={`Contact · ${selected.tenantName}`} onClose={close}>
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: 8, fontSize: '0.9rem' }}>
              <span className="text-muted text-sm" style={{ paddingTop: 2 }}>Tenant</span><span className="font-medium">{selected.tenantName}</span>
              <span className="text-muted text-sm" style={{ paddingTop: 2 }}>Plan</span>
              <span>{selected.planName} <span className="text-muted text-sm">· {selected.billingCycle ? selected.billingCycle.toLowerCase() : '—'}</span></span>
              <span className="text-muted text-sm" style={{ paddingTop: 2 }}>Renews</span>
              <span>{new Date(selected.renewsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · {selected.amount.toLocaleString('en-IN', { style: 'currency', currency: selected.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
              <span className="text-muted text-sm" style={{ paddingTop: 2 }}>Email</span>
              <span>{selected.contactEmail ?? <span className="text-muted">— no email on file</span>}</span>
              <span className="text-muted text-sm" style={{ paddingTop: 2 }}>Phone</span>
              <span>{selected.contactPhone ?? <span className="text-muted">— no phone on file</span>}</span>
            </div>
            {notice && <p className="text-sm mt-2" style={{ color: notice.startsWith('Error') ? 'var(--danger)' : 'var(--success)', margin: '10px 0 0' }}>{notice}</p>}
            <div className="mt-4 flex gap-2">
              <Button variant="secondary" disabled={busy === 'whatsapp' || !selected.contactPhone} onClick={() => sendWhatsApp(selected)}>
                {busy === 'whatsapp' ? 'Opening…' : 'WhatsApp'}
              </Button>
              <Button disabled={busy === 'email' || !selected.contactEmail} onClick={() => sendEmail(selected)}>
                {busy === 'email' ? 'Sending…' : 'Send Email'}
              </Button>
            </div>
            <p className="text-muted text-sm mt-2" style={{ margin: '10px 0 0' }}>
              Message templates are editable under Settings → Renewal Reminders.
            </p>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
}
